// "Get the Atlas app" prompt for mobile-web visitors without the app
// installed. Pure show/hide + copy logic, kept separate from the component so
// the rules (UA, path, dismissal, audience → show?) are unit-testable without
// a DOM. See docs/... (atlas-mobile handoff, 2026-09-02, §2) for the spec.

/** Derived the same way atlas-mobile derives it: from the session's app
 * permissions, never a separate org-class column. Portal customers aren't
 * launched in the app yet, so 'portal' exists here only to be excluded. */
export type MobileAppAudience =
  | 'technician'
  | 'internal'
  | 'portal'
  | 'signed_out'

export type MobileAppBannerPlatform = 'ios' | 'android'

const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000

/** Route prefixes/patterns that must never show the prompt — mirrors the
 * `NOT` entries in the AASA universal-link exclusions, so the banner and the
 * deep-link claim stay in sync. */
const EXCLUDED_PATH_PATTERNS: RegExp[] = [
  /^\/accept-invitation\//,
  // Also the bare `/reset-password` the page leaves after taking the token
  // off the address bar.
  /^\/reset-password(?:[/?]|$)/,
  /^\/forgot-password/,
  // `/sign-in` and the org-scoped `/<orgSlug>/sign-in`.
  /^(?:\/[^/]+)?\/sign-in(?:[/?]|$)/
]

function isExcludedMobileAppBannerPath(pathname: string): boolean {
  return EXCLUDED_PATH_PATTERNS.some((pattern) => pattern.test(pathname))
}

/** iOS devices report Safari UNLESS the visitor is in another browser, which
 * still carries "AppleWebKit" and "Mobile/" but not "Safari" is wrong —
 * Chrome/Firefox on iOS both inject "CriOS"/"FxiOS" ahead of "Safari" in the
 * UA string, so checking for those tokens first is what actually
 * distinguishes them from real Safari. */
export function detectMobilePlatform(
  userAgent: string
): MobileAppBannerPlatform | null {
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios'
  if (/Android/.test(userAgent)) return 'android'
  return null
}

export function isIosSafari(userAgent: string): boolean {
  if (!/iPhone|iPad|iPod/.test(userAgent)) return false
  if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent)) return false
  return /Safari/.test(userAgent)
}

export interface DeriveMobileAppAudienceInput {
  isSignedIn: boolean
  isInternal: boolean
  accessibleApps: readonly string[]
}

/** Audience priority when a power user holds more than one app: technician
 * first (the pilot's own audience and the reason this shipped), then any
 * other internal access, then portal (excluded downstream). */
export function deriveMobileAppAudience(
  input: DeriveMobileAppAudienceInput
): MobileAppAudience {
  if (!input.isSignedIn) return 'signed_out'
  if (input.accessibleApps.includes('technician')) return 'technician'
  if (input.isInternal) return 'internal'
  if (input.accessibleApps.includes('portal')) return 'portal'
  return 'signed_out'
}

export const MOBILE_APP_BANNER_COPY: Record<
  Exclude<MobileAppAudience, 'portal'>,
  string
> = {
  technician: 'Get dispatch alerts and capture photos from the field.',
  internal: 'Get push alerts for cases and work orders, and Ask Vue on the go.',
  signed_out: 'Atlas on your phone: dispatch alerts and photo capture.'
}

export interface ShouldShowMobileAppBannerInput {
  pathname: string
  isPhoneWidth: boolean
  userAgent: string
  audience: MobileAppAudience
  /** Epoch ms of the last dismissal, or null if never dismissed. */
  dismissedAt: number | null
  now: number
}

/** The one place every visibility rule from the spec is combined, so the
 * component only has to ask "show?" and never re-derive a rule. */
export function shouldShowMobileAppBanner(
  input: ShouldShowMobileAppBannerInput
): boolean {
  // Customers aren't launched in the app yet — never show them a prompt for
  // something they can't use.
  if (input.audience === 'portal') return false
  if (!input.isPhoneWidth) return false
  if (isExcludedMobileAppBannerPath(input.pathname)) return false

  const platform = detectMobilePlatform(input.userAgent)
  if (!platform) return false
  // iOS Safari already gets Apple's own Smart App Banner (the
  // `apple-itunes-app` meta tag in the root layout) — a second prompt there
  // would be redundant chrome stacked on top of Apple's.
  if (platform === 'ios' && isIosSafari(input.userAgent)) return false

  if (
    input.dismissedAt !== null &&
    input.now - input.dismissedAt < DISMISS_TTL_MS
  ) {
    return false
  }

  return true
}

export const MOBILE_APP_STORE_URLS: Record<MobileAppBannerPlatform, string> = {
  ios: 'https://apps.apple.com/app/id6761316428',
  // The https form, never `market://` — that scheme only resolves on-device
  // and does nothing in a desktop or mobile browser.
  android:
    'https://play.google.com/store/apps/details?id=com.worldvue.atlasmobile'
}
